/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React, { Fragment } from "react"
import { Flex, Tooltip } from "@duxton-web/ui"
import { MoreInfoOutlined } from "@duxton-web/icons"
import { Review, ReviewStatus } from "types/api/menuFeedback.type"
import { useTranslationsMap, useLocaleContext } from "common/utils/i18n"
import {
  DEFAULT_DATE_FORMAT,
  getDayjsLocale,
  getLocalizedDateFormat,
  TAIWAN_DATE_FORMAT,
} from "utils/i18n"
import dayjs from "dayjs"
import { ServiceType } from "constants/feedback"

type StatusColumnProp = {
  review: Review
  displayServiceTypeLabel?: boolean
  hasSelfPickup?: boolean
}

type StyleObject = {
  [key: string]: any
}

const StatusColumn: React.FC<StatusColumnProp> = ({
  review,
  displayServiceTypeLabel,
  hasSelfPickup,
}) => {
  let statusClassStyle

  const textMap = useTranslationsMap({
    public: "feedback.statuses.public",
    reported: "feedback.statuses.reported",
    removed: "feedback.statuses.removed",
    visibleToGrabUsers: "feedback.tooltips.visibleToGrabUsers",
    wasReported: "feedback.tooltips.wasReported",
    hasRemoved: "feedback.tooltips.hasRemoved",
    notVisibleToGrabUsers: "feedback.tooltips.notVisibleToGrabUsers",
    dineIn: "feedback.filters.serviceType.dineIn",
    delivery: "feedback.filters.serviceType.delivery",
    deliveryAndSelfPickup: "feedback.filters.serviceType.deliveryAndSelfPickup",
    nonTransactional: "feedback.labels.serviceType.nonTransactional",
  })

  const locale = useLocaleContext("locale")
  const dayjsLocale = getDayjsLocale(locale)
  const dateFormat = getLocalizedDateFormat(
    locale,
    DEFAULT_DATE_FORMAT,
    TAIWAN_DATE_FORMAT,
  )

  const serviceTypeText: { [key: string]: string } = {
    [ServiceType.DELIVERY]: hasSelfPickup
      ? textMap.deliveryAndSelfPickup
      : textMap.delivery,
    [ServiceType.DINEIN]: textMap.dineIn,
    [ServiceType.NON_TRANSACTIONAL]: textMap.nonTransactional,
  }

  const generateStatusClassStyle = (additionalStyle: StyleObject) => {
    return css({
      ...additionalStyle,
      display: "flex",
      flexDirection: "row",
      gap: "8px",
      alignItems: "center",
    })
  }

  const generateStatusBackgroundClassStyle = (
    additionalStyle?: StyleObject,
  ) => {
    return css({
      ...additionalStyle,
      lineHeight: "16px",
      textTransform: "lowercase",
      borderRadius: "4px",
      padding: "6px",
      width: "fit-content",
    })
  }

  const generateStatusBackground = () => {
    switch (review.status) {
      case ReviewStatus.PUBLIC:
        return generateStatusBackgroundClassStyle({
          backgroundColor: "#EBFAFF",
        })
      case ReviewStatus.REPORTED:
      case ReviewStatus.REMOVED:
      case ReviewStatus.UNKNOWN:
      case ReviewStatus.PRIVATE:
        return generateStatusBackgroundClassStyle({
          backgroundColor: "#FFF9C0",
        })
      default:
        return generateStatusBackgroundClassStyle({})
    }
  }

  const renderStatusPopoverMessage = () => {
    switch (review.status) {
      case ReviewStatus.PUBLIC:
        return textMap.visibleToGrabUsers
      case ReviewStatus.REPORTED:
        return textMap.wasReported
          .replace(
            /<date.short>/gi,
            `${dayjs(review.reportedAt)
              .locale(dayjsLocale)
              .format(dateFormat)}`,
          )
          .replace(/<name>/gi, `${review.eaterName}`)
      case ReviewStatus.REMOVED:
        return textMap.hasRemoved
      case ReviewStatus.PRIVATE:
        return textMap.notVisibleToGrabUsers
      case ReviewStatus.UNKNOWN:
      default:
        return ""
    }
  }

  const renderStatusCard = (statusClassStyleOverride: any) => (
    <div css={statusClassStyleOverride}>
      <MoreInfoOutlined />
      <p
        css={css({
          marginBottom: "0px",
          ":first-letter": {
            textTransform: "uppercase",
          },
        })}
      >
        {textMap[review.status.toLowerCase()] || review.status}
      </p>
    </div>
  )

  const renderStatusText = (status: ReviewStatus) => {
    switch (status) {
      case ReviewStatus.PUBLIC:
        statusClassStyle = generateStatusClassStyle({ color: "#0B54A8" })
        return renderStatusCard(statusClassStyle)
      case ReviewStatus.REPORTED:
      case ReviewStatus.REMOVED:
      case ReviewStatus.UNKNOWN:
      case ReviewStatus.PRIVATE:
        statusClassStyle = generateStatusClassStyle({ color: "#824703" })
        return renderStatusCard(statusClassStyle)
      default:
        return <Fragment />
    }
  }

  return (
    <Flex vertical align="center">
      {displayServiceTypeLabel && review.serviceType && (
        <p
          css={css({
            color: "#707070",
            marginBottom: "8px",
          })}
        >
          {serviceTypeText[review.serviceType] || review.serviceType}
        </p>
      )}
      <Tooltip
        overlay={<p>{renderStatusPopoverMessage()}</p>}
        placement="bottom"
      >
        <div css={css(generateStatusBackground())}>
          {renderStatusText(review.status)}
        </div>
      </Tooltip>
    </Flex>
  )
}

export default React.memo<StatusColumnProp>(StatusColumn)
